<template>
  <div class="section-content l">

    <!-- HEADER — title · tabs (right-aligned) · dot — all one line -->
    <div class="section-header section-header-alt">
      <div class="section-title">STATUS :</div>

      <div v-if="hasSimCard || isDlmsWater" class="tab-bar">
        <button type="button"
            class="tab-btn"
            :class="{ active: activeTab === 'status' }"
            @click="activeTab = 'status'"
        >Device</button>
        <button type="button"
            v-if="hasSimCard"
            class="tab-btn"
            :class="{ active: activeTab === 'sim' }"
            @click="onSimTabClick"
        >Sim
          <span v-if="simLoading && activeTab !== 'sim'" class="tab-loading-dot"></span>
        </button>
        <button v-if="isDlmsWater" type="button" class="tab-btn tasking-tab"
                :class="{ active: activeTab === 'tasking' }" :aria-pressed="activeTab === 'tasking'"
                @click="activeTab = 'tasking'">Tasking</button>
      </div>

      <div class="status-indicator" :class="statusIconClass"></div>
    </div>

    <!-- ─────────────── TAB: DEVICE STATUS ─────────────── -->
    <div v-if="activeTab === 'status'">
      <div v-if="isLoading" class="loading-state">
        <div class="loading-spinner"></div>
        <span>Loading status data...</span>
      </div>

      <div v-else class="info-grid">
        <div class="info-row">
          <div class="info-label">Has readings :</div>
          <div class="info-value" :class="{'status-yes': hasLatestReading, 'status-no': !hasLatestReading}">
            {{ hasLatestReading ? 'Yes' : 'No' }}
          </div>
        </div>
        <div class="info-row">
          <div class="info-label">Attached to MPID :</div>
          <div class="info-value" :class="{'status-yes': hasMpid, 'status-no': !hasMpid}">
            {{ hasMpid ? 'Yes' : 'No' }}
          </div>
        </div>
        <div class="info-row">
          <div class="info-label">Value :</div>
          <div class="info-value">
            {{ formattedLatestValue }}
          </div>
        </div>
        <div class="info-row">
          <div class="info-label">Alarms :</div>
          <div class="info-value" :class="{'has-alarms': hasAlarms}">
            {{ hasAlarms ? 'Yes' : 'No' }}
          </div>
        </div>
        <div class="info-row">
          <div class="info-label">Last Reading :</div>
          <div class="info-value">{{ formattedLastReadingTime }}</div>
        </div>
        <!-- Battery — only for LORA_WBUS devices -->
        <div v-if="showBattery" class="info-row">
          <div class="info-label">Battery :</div>
          <div class="info-value" :class="batteryLevelClass">
            {{ batteryLevel !== null ? `${batteryLevel} V` : 'N/A' }}
          </div>
        </div>
      </div>
    </div>

    <DeviceDetailsTasking v-else-if="activeTab === 'tasking' && isDlmsWater"
                          :key="deviceId" :device-id="deviceId" />

    <!-- ─────────────── TAB: SIM STATUS ─────────────── -->
    <div v-else-if="activeTab === 'sim'">

      <!-- Loading -->
      <div v-if="simLoading" class="loading-state">
        <div class="loading-spinner"></div>
        <span>Loading SIM data...</span>
      </div>

      <!-- Error -->
      <div v-else-if="simError" class="error-state">
        <div class="error-message">{{ simError }}</div>
        <button class="retry-button" @click="fetchSimData">Retry</button>
      </div>

      <!-- Content: graph as background wallpaper, info on top -->
      <div v-else-if="simStatus" class="sim-container">

        <!-- Background graph — position absolute, opacity 0.15 -->
        <div class="bg-canvas">
          <div
              v-for="(row, ri) in activityRows"
              :key="ri"
              class="bg-row"
          >
            <div
                v-for="(day, di) in row"
                :key="di"
                class="bg-cell"
                :class="day.level"
                :title="day.date"
            ></div>
          </div>
        </div>

        <!-- Info overlay — z-index above graph -->
        <div class="sim-info">
          <div class="info-row">
            <div class="info-label">Service :</div>
            <div class="info-value">{{ simStatus.vf_services || 'N/A' }}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Customer :</div>
            <div class="info-value">{{ simStatus.vf_customer_name || 'N/A' }}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Data In :</div>
            <div class="info-value">{{ formatBytes(simStatus.vf_bytes_in) }}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Data Out :</div>
            <div class="info-value">{{ formatBytes(simStatus.vf_bytes_out) }}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Last Check :</div>
            <div class="info-value">{{ formatDateTime(simStatus.last_vodafone_check) }}</div>
          </div>
        </div>
      </div>

      <!-- No SIM data -->
      <div v-else class="empty-state-sim">
        <p>No SIM data available</p>
      </div>

    </div>
  </div>
</template>

<script>
import DeviceDetailsTasking from './DeviceDetailsTasking.vue';
import { getAlarmStatus } from '@/services/alarmHelper';
import { amrRawToM3, formatM3 } from '@/services/loraWbusHelper';

export default {
  name: 'DeviceDetailsStatusHistory',
  components: { DeviceDetailsTasking },
  props: {
    deviceId:       { type: String,  default: '' },
    hasMpid:        { type: Boolean, default: false },
    device:         { type: Object,  default: null },
    deviceProtocol: { type: String,  default: '' }
  },
  data() {
    return {
      latestReading:  null,
      isLoading:      false,
      error:          '',
      hasAlarms:      false,
      batteryLevel:   null,
      deviceHistory:  [],
      historyLoading: false,
      historyError:   '',
      activeTab:      'status',
      simStatus:      null,
      simHistory:     [],
      simLoading:     false,
      simError:       '',
      simFetched:     false,
      graphWeeks:     16
    };
  },
  computed: {
    isDlmsWater() {
      return ['dlms', 'dlmswater', 'dlmsgas', '2001'].includes(String(this.deviceProtocol || this.device?.protocol || '').trim().toLowerCase());
    },
    hasSimCard() {
      return !!this.device?.iccid;
    },
    hasLatestReading() {
      return this.latestReading !== null;
    },
    isLoraWbusProtocol() {
      const protocol = String(this.deviceProtocol || '').toLowerCase();
      return protocol === 'lora' || protocol === 'wbus' || protocol === 'lora_wbus';
    },
    formattedLatestValue() {
      if (!this.latestReading) return 'N/A';

      if (this.isLoraWbusProtocol) {
        const additionalData = this.latestReading.additional_data || {};
        const rawValue =
            additionalData.Volume?.Raw ??
            additionalData.Volume?.Engineered ??
            this.latestReading.current_reading;
        const m3Value = amrRawToM3(rawValue, additionalData);

        if (m3Value !== null) return formatM3(m3Value);
      }

      return this.latestReading.current_reading !== undefined ? this.latestReading.current_reading : 'N/A';
    },
    lastReadingTimestamp() {
      const additionalData = this.latestReading?.additional_data || {};
      if (additionalData.Timestamp) return additionalData.Timestamp;

      const unixTime = Number(additionalData.UnixTime);
      if (Number.isFinite(unixTime) && unixTime > 0) return unixTime * 1000;

      return this.latestReading?.date_current_reading || this.latestReading?.created_at;
    },
    formattedLastReadingTime() {
      if (!this.lastReadingTimestamp) return 'N/A';
      return this.formatDateTime(this.lastReadingTimestamp);
    },
    statusIconClass() {
      if (this.hasLatestReading && this.hasMpid) return 'status-icon-green';
      if (this.hasLatestReading || this.hasMpid)  return 'status-icon-yellow';
      return 'status-icon-red';
    },
    showBattery() {
      return this.isLoraWbusProtocol && this.batteryLevel !== null;
    },
    batteryLevelClass() {
      if (this.batteryLevel === null) return '';
      if (this.batteryLevel > 3.0)   return 'battery-good';
      if (this.batteryLevel >= 2.8)  return 'battery-warning';
      return 'battery-critical';
    },

    // ── Activity graph ────────────────────────────────────
    activityByDate() {
      const map = {};
      for (const entry of this.simHistory) {
        const date = entry.ts?.substring(0, 10);
        if (!date) continue;
        if (!map[date]) map[date] = { lastSeen: 0, simStatus: 0 };
        if (entry.type === 'last_seen')  map[date].lastSeen++;
        if (entry.type === 'sim_status') map[date].simStatus++;
      }
      return map;
    },

    activityGrid() {
      const weeks     = this.graphWeeks;
      const totalDays = weeks * 7;
      const today     = new Date(); today.setHours(0, 0, 0, 0);
      const endDate   = new Date(today); endDate.setDate(endDate.getDate() + (6 - today.getDay()));
      const startDate = new Date(endDate); startDate.setDate(endDate.getDate() - totalDays + 1);

      const days = [];
      for (let i = 0; i < totalDays; i++) {
        const d   = new Date(startDate); d.setDate(startDate.getDate() + i);
        const key = d.toISOString().substring(0, 10);
        const act = this.activityByDate[key] || { lastSeen: 0, simStatus: 0 };

        let level   = 'level-0';
        let tooltip = 'No activity';
        if (act.lastSeen > 0 && act.simStatus === 0) {
          level = 'level-1'; tooltip = `${act.lastSeen} last_seen event(s)`;
        } else if (act.simStatus > 0 && act.lastSeen === 0) {
          level = 'level-2'; tooltip = `${act.simStatus} SIM check(s)`;
        } else if (act.lastSeen > 0 && act.simStatus > 0) {
          level = 'level-3'; tooltip = `${act.lastSeen} last_seen + ${act.simStatus} SIM check(s)`;
        }
        days.push({ date: key, level: d > today ? 'level-future' : level, tooltip });
      }

      const grid = [];
      for (let w = 0; w < weeks; w++) {
        grid.push(days.slice(w * 7, w * 7 + 7));
      }
      return grid;
    },

    // Transpose activityGrid [week][day] → [day][week] for row-based background rendering
    activityRows() {
      const grid = this.activityGrid;
      const rows = [];
      for (let d = 0; d < 7; d++) {
        const row = [];
        for (let w = 0; w < grid.length; w++) {
          row.push(grid[w][d]);
        }
        rows.push(row);
      }
      return rows;
    }
  },
  watch: {
    deviceId: { immediate: true, handler(v) { this.activeTab = 'status'; if (v) this.fetchLatestReading(); } },
    isDlmsWater(v) { if (!v && this.activeTab === 'tasking') this.activeTab = 'status'; },
    device:   { immediate: true, deep: true, handler(v) { if (v) this.generateDeviceHistory(v); } }
  },
  methods: {
    onSimTabClick() {
      this.activeTab = 'sim';
      if (!this.simFetched) this.fetchSimData();
    },

    async fetchLatestReading() {
      if (!this.deviceId) return;
      this.isLoading = true;
      this.error     = '';
      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const res = await fetch(`${apiBaseUrl}/readings/device/${this.deviceId}/latest`, {
          headers: {
            'accept':        'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        if (!res.ok) throw new Error(res.statusText);
        this.latestReading = await res.json();
        this.hasAlarms     = getAlarmStatus(this.latestReading).hasAlarms;
        this.batteryLevel  =
            this.isLoraWbusProtocol &&
            this.latestReading?.additional_data?.BatteryLevel !== undefined
                ? this.latestReading.additional_data.BatteryLevel
                : null;
      } catch (e) {
        console.error('Error fetching latest reading:', e);
        this.error         = 'Failed to load latest reading data';
        this.latestReading = null;
        this.batteryLevel  = null;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchSimData() {
      if (!this.deviceId) return;
      this.simLoading = true;
      this.simError   = '';
      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const headers    = {
          'accept':        'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        };
        const [statusRes, historyRes] = await Promise.all([
          fetch(`${apiBaseUrl}/sim/devices/${this.deviceId}`,         { headers }),
          fetch(`${apiBaseUrl}/sim/devices/${this.deviceId}/history`, { headers })
        ]);
        if (!statusRes.ok)  throw new Error(`SIM status error: ${statusRes.statusText}`);
        if (!historyRes.ok) throw new Error(`SIM history error: ${historyRes.statusText}`);
        this.simStatus  = await statusRes.json();
        const hData     = await historyRes.json();
        this.simHistory = hData.entries || [];
        this.simFetched = true;
      } catch (e) {
        console.error('Error fetching SIM data:', e);
        this.simError = 'Failed to load SIM data';
      } finally {
        this.simLoading = false;
      }
    },

    generateDeviceHistory(device) {
      if (!device) return;
      this.historyLoading = true;
      try {
        const history = [];
        if (device.installed) history.push({ timestamp: new Date(device.installed), action: 'DEVICE CREATED' });
        if (device.mpid)      history.push({ timestamp: new Date(),                 action: `ATTACHED TO MPID ${device.mpid}` });
        if (device.last_active_mpid && Array.isArray(device.last_active_mpid)) {
          device.last_active_mpid.forEach(r =>
              history.push({ timestamp: new Date(r.from_time), action: `ATTACHED TO MPID ${r.mpid}` })
          );
        }
        this.deviceHistory = history.sort((a, b) => b.timestamp - a.timestamp);
      } catch (e) {
        console.error('Error generating device history:', e);
        this.historyError = 'Failed to generate device history';
      } finally {
        this.historyLoading = false;
      }
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      try {
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return 'Invalid Date';
        const dd  = String(d.getDate()).padStart(2, '0');
        const mm  = String(d.getMonth() + 1).padStart(2, '0');
        const hh  = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${dd}/${mm}/${d.getFullYear()} ${hh}:${min}`;
      } catch { return 'Error'; }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return 'Invalid Date';
        return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
      } catch { return 'Error'; }
    },
    formatBytes(raw) {
      const n = parseInt(raw, 10);
      if (isNaN(n))    return 'N/A';
      if (n === 0)     return '0 B';
      if (n < 1024)    return `${n} B`;
      if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
      return `${(n / 1048576).toFixed(2)} MB`;
    }
  }
};
</script>

<style scoped>
.section-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.l {
  padding-left: 50px;
}

/* ── Header — STATUS · tabs · dot on one line ────────────── */
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.section-header-alt {
  margin-top: 80px;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: white;
  white-space: nowrap;
}

/* ── Tab bar — pushed to the right ──────────────────────── */
.tab-bar {
  margin-left: auto;
  display: flex;
  gap: 8px;
  align-items: center;
  margin-right: 12px;
}

.tab-btn {
  background: transparent;
  border: 0;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  font-family: inherit;
  color: hsla(0, 0%, 100%, 0.45);
  white-space: nowrap;
  padding: 2px 4px;
  user-select: none;
  transition: color 0.15s;
  display: flex;
  align-items: center;
  gap: 5px;
}

.tasking-tab { background: transparent; border: 0; }

.tab-btn.active {
  color: #ffffff;
}

.tab-btn:hover:not(.active) {
  color: hsla(0, 0%, 100%, 0.65);
}

.tab-loading-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #fbbf24;
  animation: pulse 1.5s ease-in-out infinite;
}

/* ── Status indicator dot ────────────────────────────────── */
.status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-icon-green {
  background: #4ade80;
  box-shadow: 0 0 12px #4ade80;
  animation: pulse 2s ease-in-out infinite;
}

.status-icon-yellow {
  background: #fbbf24;
  box-shadow: 0 0 12px #fbbf24;
}

.status-icon-red {
  background: #f87171;
  box-shadow: 0 0 12px #f87171;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
}

/* ── Info grid — matches DeviceDetailsInfo pattern ───────── */
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-top: 35px;
}

.info-row {
  margin-bottom: 25px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.info-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.05em;
  opacity: 0.6;
  text-transform: uppercase;
  color: white;
}

.info-value {
  font-size: 11px;
  font-weight: 500;
  color: white;
}

.status-yes  { color: #4ade80 !important; }
.status-no   { color: #f87171 !important; }
.has-alarms  { color: #f87171 !important; }

/* ── Battery ─────────────────────────────────────────────── */
.battery-good     { color: #4ade80 !important; }
.battery-warning  { color: #fbbf24 !important; }
.battery-critical { color: #f87171 !important; }

/* ── SIM container — graph wallpaper + info overlay ─────── */
.sim-container {
  position: relative;
  overflow: hidden;
  border-radius: 4px;
}

/* Background graph — fills container, 15% opacity */
.bg-canvas {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 3px;
  opacity: 0.7;
}

.bg-row {
  display: flex;
  gap: 3px;
}

.bg-cell {
  flex: 1;
  aspect-ratio: 1;
  border-radius: 2px;
}

/* Cell activity levels */
.level-0      { background: rgba(255, 255, 255, 0.30); }
.level-1      { background: rgba(74, 222, 128, 0.60); }
.level-2      { background: rgba(74, 222, 128, 0.80); }
.level-3      { background: rgba(74, 222, 128, 1.00); }
.level-future { background: rgba(255, 255, 255, 0.10); }

/* Info overlay — sits above graph */
.sim-info {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding-top: 35px;
}

/* ── States ──────────────────────────────────────────────── */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 120px;
  gap: 12px;
}

.error-message {
  color: #f87171;
  font-size: 12px;
  text-align: center;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.empty-state-sim {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 140px;
  gap: 10px;
  opacity: 0.5;
}

.empty-state-sim p {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0;
}

/* ── Responsive ──────────────────────────────────────────── */
@media (max-width: 767.98px) {
  .section-header-alt { margin-top: 60px; }
  .l                  { padding-left: 30px; }
  .info-row           { margin-bottom: 15px; }
}
</style>
